// MarketPage — German. The console's language; the record's own content stays French.
// Shared words ('not collected', 'Loading…', 'Nothing matches', 'Clear the filter', 'Show {n}
// more', 'Tier', 'Status', 'Organisations', 'not scored') already live in common.de.ts or in
// another page's catalogue and are not repeated here: a key translated twice warns on the
// console and the first registration wins.
//
// The RASTER's own vocabulary — block, cell, band, sub-sector and canton names — is NOT here.
// It lives in `src/shared/raster.v1.ts` beside the value it belongs to, and the page reads it
// through `labelFor`. A sub-sector spelled a second time in this file would be a second source
// of truth that drifts.
import { registerDe } from '../i18n/index.js';

registerDe({
  'Organisation raster · v1': 'Organisationsraster · v1',
  'Every organisation against one raster of comparable cells — who is a target, and why.':
    'Jede Organisation an einem Raster vergleichbarer Zellen gemessen — wer ein Ziel ist und warum.',
  'every organisation against one raster of comparable cells':
    'jede Organisation an einem Raster vergleichbarer Zellen',
  'profiled': 'profiliert',

  // the rail
  'Profiled': 'Profiliert',
  'organisations holding at least one profile cell':
    'Organisationen mit mindestens einer Profilzelle',
  'nobody has filled a single raster cell': 'niemand hat eine einzige Rasterzelle gefüllt',
  'Coverage': 'Abdeckung',
  'median share of the comparable cells that are filled':
    'Mediananteil der ausgefüllten vergleichbaren Zellen',
  'no organisation is profiled, so there is no median to take':
    'keine Organisation ist profiliert, also gibt es keinen Median',
  'Targets': 'Ziele',
  '{w} watch · {p} park · {e} exclude': '{w} beobachten · {p} parkiert · {e} ausgeschlossen',
  'Sub-sectors': 'Teilbereiche',
  'distinct sub-sectors on the record': 'verschiedene Teilbereiche im Bestand',
  'no sub-sector cell has been filled': 'keine Teilbereichszelle wurde gefüllt',
  'Triggers 30d': 'Auslöser 30 T',
  'trigger cells standing true on a computation from the last 30 days':
    'Auslöserzellen, die in einer Berechnung der letzten 30 Tage wahr sind',
  'the market did not answer': 'der Markt hat nicht geantwortet',
  'counting the population…': 'die Grundgesamtheit wird gezählt…',
  'nothing has answered yet': 'bisher hat nichts geantwortet',

  // the filter bands
  'Name, legal name, ref…': 'Name, Firmenname, Referenz…',
  'Search the population': 'Grundgesamtheit durchsuchen',
  'All': 'Alle',

  // the grid
  'pick two rows to compare their rasters': 'zwei Zeilen wählen, um ihre Raster zu vergleichen',
  'one picked · pick a second to compare': 'eine gewählt · eine zweite wählen zum Vergleich',
  'The market did not answer': 'Der Markt hat nicht geantwortet',
  'No organisation on the record matches these filters.':
    'Keine Organisation im Bestand passt zu diesen Filtern.',
  'The record holds no active organisation. Organisations arrive from the seed import, from a directory monitor, or from a proposal you hold.':
    'Der Bestand enthält keine aktive Organisation. Organisationen kommen aus dem Seed-Import, aus einem Verzeichnis-Monitor oder aus einem Vorschlag, den Sie übernehmen.',
  'No organisation holds a single raster cell yet, so every cell below reads "not collected". Open an organisation and use "Fill profile": it starts a research run against the raster, and every value it reads comes back as a proposal with its verbatim quote.':
    'Noch hält keine Organisation eine einzige Rasterzelle, deshalb steht unten überall «nicht erhoben». Öffnen Sie eine Organisation und wählen Sie «Profil füllen»: das startet einen Rechercheauftrag gegen das Raster, und jeder gelesene Wert kommt als Vorschlag mit wörtlichem Zitat zurück.',
  'The last refresh failed ({error}). The figures below are the previous answer.':
    'Die letzte Aktualisierung schlug fehl ({error}). Die Zahlen unten sind die vorherige Antwort.',
  'Pick': 'Wählen',
  'Pick {name} for the comparison': '{name} für den Vergleich wählen',
  'Fit': 'Passung',
  'Access': 'Zugang',
  'Composite': 'Produkt',
  'Why': 'Warum',
  'hide': 'ausblenden',
  '{shown} of {total}': '{shown} von {total}',
  'This organisation has not been scored, so no rule has fired for it.':
    'Diese Organisation wurde nicht bewertet, also hat keine Regel gegriffen.',
  'No rule fired. The composite is the product of the three floors — the honest score for an organisation nothing is known about.':
    'Keine Regel hat gegriffen. Das Produkt ist das der drei Untergrenzen — der ehrliche Wert für eine Organisation, über die nichts bekannt ist.',
  'fit': 'Passung',
  'access': 'Zugang',

  // the four tiers
  'target': 'Ziel',
  'watch': 'beobachten',
  'park': 'parkiert',
  'exclude': 'ausgeschlossen',

  // the comparison
  'Compare': 'Vergleich',
  'the same raster, cell by cell — a row empty on both sides is a fact about our coverage':
    'dasselbe Raster, Zelle für Zelle — eine beidseitig leere Zeile ist eine Aussage über unsere Abdeckung',

  // the population
  'Population': 'Grundgesamtheit',
  'the federation lists and cantonal registers the names came from':
    'die Mitgliederlisten der Dachverbände und die kantonalen Register, aus denen die Namen stammen',
  'The directories could not be read: {error}':
    'Die Verzeichnisse konnten nicht gelesen werden: {error}',
  'No directory is registered yet. A directory is a federation member list — AVOP, Fachverband Sucht, the DGCS list of établissements socio-éducatifs — whose unknown names arrive as organisation proposals with the list line as their verbatim quote.':
    'Es ist noch kein Verzeichnis erfasst. Ein Verzeichnis ist eine Mitgliederliste eines Dachverbands — AVOP, Fachverband Sucht, die DGCS-Liste der établissements socio-éducatifs —, deren unbekannte Namen als Organisationsvorschläge mit der Listenzeile als wörtlichem Zitat eintreffen.',
  'Directory': 'Verzeichnis',
  'Federation': 'Dachverband',
  'Members seen': 'Erfasste Mitglieder',
  'Waiting as proposals': 'Als Vorschlag wartend',
  'Last import': 'Letzter Import',
  'never imported': 'nie importiert',

  // ── the units and the from-parent fallback ──
  'Top-level only': 'Nur oberste Ebene',
  'A unit is a department, a site or a programme under another organisation. With this on, the raster compares organisations only.':
    'Eine Einheit ist eine Abteilung, ein Standort oder ein Programm unter einer anderen Organisation. Ist dies an, vergleicht das Raster nur Organisationen.',
  '{n} units hidden by “Top-level only”': '{n} Einheiten durch „Nur oberste Ebene“ ausgeblendet',
  '{n} units hidden among the rows loaded': '{n} Einheiten unter den geladenen Zeilen ausgeblendet',
  'counted among the rows loaded; the units in the rest of the set were not counted':
    'unter den geladenen Zeilen gezählt; die Einheiten im übrigen Satz wurden nicht gezählt',
  'Every organisation these filters match is a unit of another one, and “Top-level only” is on.':
    'Jede Organisation, die auf diese Filter passt, ist eine Einheit einer anderen, und „Nur oberste Ebene“ ist an.',
  'unit of': 'Einheit von',
  'another organisation': 'einer anderen Organisation',
  'from parent': 'von übergeordnet',
  'held by {name}, this unit’s parent — not a fact about the unit':
    'von {name} übernommen, der übergeordneten Zeile dieser Einheit — keine Aussage über die Einheit selbst',
  'held by this unit’s parent — not a fact about the unit':
    'von der übergeordneten Zeile dieser Einheit übernommen — keine Aussage über die Einheit selbst',
  'Review': 'Prüfung',
}, 'MarketPage');
